
import { Component } from "vue-property-decorator";
import HospitalHeader from "@/components/HospitalHeader.vue";
import ManagementFooter from "@/components/ManagementFooter.vue";
import Button from "@/components/Button.vue";
import FacilityService from "@/services/facilities";
import MemberService from "@/services/members";
import FacilityModule from "@/store/facility";
import { ReservationListItem, ReservationListRow } from "@/@types/reservation";
import CheckCommonLogin from "@/utils/CheckCommonLogin";
import Papa from "papaparse";
import Encoding from "encoding-japanese";

@Component({
  name: "ReservationList",
  components: {
    HospitalHeader,
    ManagementFooter,
    Button,
  },
})
export default class ReservationList extends CheckCommonLogin {
  // 適用中の抽出期間（初期値: queryのdate、なければ当日）
  private appliedRange: { start: string; end: string } = { start: "", end: "" };

  // カレンダーモーダル制御
  private showCalendarDialog = false;
  // モーダル内で仮選択中の範囲（決定で appliedRange に反映）
  private pendingRange: { start: Date | null; end: Date | null } = { start: null, end: null };
  // 範囲選択ドラッグ中フラグ（1日目クリック〜2日目クリックの間 true）
  // この間は「指定範囲を抽出」ボタンを非活性にして、1日目だけで誤確定するのを防ぐ
  private isRangeDragging = false;
  // 今日の日付を見やすくする属性（デフォルトは白文字+背景なしで見えづらいため）
  // 薄いグレー背景 + カスタムクラスで文字色を黒に固定する
  private calendarAttributes = [
    {
      key: "today",
      highlight: {
        color: "gray",
        fillMode: "light",
        contentClass: "reservation-list-today-content",
      },
      dates: new Date(),
    },
  ];

  // APIから取得した予約一覧（生データ）
  private items: ReservationListItem[] = [];
  // 表示用に「どうぶつ単位」で展開した行（マスタ）
  private rows: ReservationListRow[] = [];
  // 通信中フラグ
  private loading = false;

  // CSVエクスポート中フラグ（ボタンのローディング表示用）
  private csvDownloading = false;

  // 印刷対象0件アラートモーダルの表示フラグ
  private showPrintEmpty = false;

  private showPrintError = false;

  private showPrintPopupBlocked = false;

  private pdfDownloading = false;

  // ページネーション
  private currentPage = 1;
  private itemsPerPage = 10;
  private readonly itemsPerPageOptions = [10, 25, 50, 100];

  // カラムソート状態（予約日のみソート可能。予約時間側のソート機能は撤去済み）
  // column: "date" | null, order: "asc" | "desc" | null
  private sortState: { column: "date" | null; order: "asc" | "desc" | null } = {
    column: null,
    order: null,
  };

  // カラムフィルター状態
  // テキスト系は文字列、複数選択系は値の配列（空文字/空配列=絞り込みなし）
  private columnFilters: {
    ownerName: string;
    ownerKana: string;
    petName: string;
    petKana: string;
    purpose: string;
    course: string[];
    type: string[];
  } = {
    ownerName: "",
    ownerKana: "",
    petName: "",
    petKana: "",
    purpose: "",
    course: [],
    type: [],
  };

  // フィルターポップオーバー状態
  private activePopoverKey: string | null = null;
  private popoverStyle: { left: string; top: string } = { left: "0px", top: "0px" };
  // テキストフィルターの仮入力値（モーダル内のローカル状態）
  private popoverTextValue = "";
  // チェックボックスフィルターの仮選択（モーダル内のローカル状態）
  private popoverCheckboxSelected: string[] = [];
  private popoverCheckboxOptions: string[] = [];

  // 予約詳細モーダル
  private showDetailDialog = false;
  private detailLoading = false;
  // クリックされた行（行のどうぶつ情報や reservation_id をモーダル側で参照する）
  private detailRow: ReservationListRow | null = null;
  // 飼い主の追加情報（メールアドレス）
  private detailUserInfo: { email: string } = { email: "" };
  // どうぶつの追加情報（品種・性別・生年月日）
  private detailPetInfo: {
    animal_breed: string;
    gender: string;
    birthday: { year: string; month: string; day: string };
  } = {
    animal_breed: "",
    gender: "",
    birthday: { year: "", month: "", day: "" },
  };

  private get hospitalId() {
    return FacilityModule.hospitalId;
  }

  async created() {
    // クエリパラメータの date を初期抽出期間に反映（なければ今日）
    const queryDate = (this.$route.query.date as string) || "";
    const initialDate = this.isValidIsoDate(queryDate) ? queryDate : this.toIsoDate(new Date());
    this.appliedRange = { start: initialDate, end: initialDate };

    await this.fetchReservations();
  }

  // "YYYY-MM-DD" 形式の妥当性チェック
  private isValidIsoDate(value: string): boolean {
    if (!value) return false;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
    const d = new Date(value);
    return !isNaN(d.getTime());
  }

  // 予約一覧APIを呼び出して取得→行展開
  private async fetchReservations() {
    if (!this.hospitalId) return;
    try {
      this.loading = true;
      const res = await FacilityService.getReservationsForList(
        this.hospitalId,
        this.appliedRange.start,
        this.appliedRange.end
      );
      this.items = (res?.data?.data ?? []) as ReservationListItem[];
      this.rows = this.expandRows(this.items);
      this.currentPage = 1;
    } catch (error) {
      console.error("[ReservationList] failed to fetch reservations:", error);
      this.items = [];
      this.rows = [];
    } finally {
      this.loading = false;
    }
  }

  // 1予約をどうぶつ数だけ行に展開する（案B: 行を分ける）
  // 1予約=1どうぶつ になったため、API レスポンス1件をそのまま行データに変換する
  private expandRows(items: ReservationListItem[]): ReservationListRow[] {
    return items.map((item) => ({
      reservation_id: item.reservation_id,
      animal_index: 0,
      date: this.extractDate(item.datetime),
      time: this.extractTime(item.datetime),
      end_time: this.extractTime(item.datetime_end ?? ""),
      course_name: item.course_name || "",
      owner_name: this.joinName(item.last_name, item.first_name),
      owner_name_kana: this.joinName(item.last_name_kana, item.first_name_kana),
      animal_name: item.animal_name ?? "",
      animal_name_kana: item.animal_name_kana ?? "",
      animal_type: item.animal_type ?? "",
      alias_id: item.alias_id?.[0] ?? "",
      tel: item.tel ?? "",
      message: item.message ?? "",
      member_id: item.member_id ?? null,
      customer_id: item.customer_id ?? null,
      pet_id: item.pet_id ?? null,
      patient_id: item.patient_id ?? null,
    }));
  }

  // "2026-06-04T09:00:00" → "2026-06-04"
  private extractDate(datetime: string): string {
    return (datetime || "").split("T")[0] ?? "";
  }
  // "2026-06-04T09:00:00" → "09:00"
  private extractTime(datetime: string): string {
    const time = (datetime || "").split("T")[1] ?? "";
    return time.slice(0, 5);
  }
  // 表示用にゼロ埋めを外す（"09:00" → "9:00"）。
  // 内部保持値（row.time）はソート用にゼロ埋めのまま維持し、表示時にのみ整形する。
  private formatTimeForDisplay(time: string): string {
    if (!time) return "";
    return time.replace(/^0/, "");
  }
  // 開始〜終了の範囲表示。"〜" の後で改行し終了を次行に置く（列幅を広げないため）。
  // 画面はセルの white-space: pre-line、PDFは \n→<br/> 変換で2行表示になる。
  // 終了が無ければ開始のみ + "〜"、開始も無ければ空文字。
  private formatTimeRange(start: string, end: string): string {
    const s = this.formatTimeForDisplay(start);
    if (!s) return "";
    const e = this.formatTimeForDisplay(end);
    return e ? `${s}〜\n${e}` : `${s}〜`;
  }
  // 姓・名を半角スペースで結合（どちらかが空なら空欄を含めない）
  private joinName(last: string | null, first: string | null): string {
    return [last, first].filter((v) => v != null && v !== "").join(" ");
  }
  // "2026-06-04" → "6月4日"（月日のゼロ埋めなし）
  private formatDateJa(date: string): string {
    const parts = (date || "").split("-").map(Number);
    if (parts.length < 3) return "";
    const [, m, d] = parts;
    return `${m}月${d}日`;
  }

  // 抽出期間の表示用ラベル（YYYY/M/D 〜 YYYY/M/D、月日のゼロ埋めなし）
  private get periodLabel(): string {
    return `${this.formatDateSlash(this.appliedRange.start)} 〜 ${this.formatDateSlash(this.appliedRange.end)}`;
  }

  // "2026-06-04" → "2026/6/4"（月日のゼロ埋めなし）
  private formatDateSlash(date: string): string {
    const parts = (date || "").split("-").map(Number);
    if (parts.length < 3) return "";
    const [y, m, d] = parts;
    return `${y}/${m}/${d}`;
  }

  // フィルター・ソートを適用した表示用の行（rows をベースに加工）
  private get displayRows(): ReservationListRow[] {
    let list = [...this.rows];

    // テキストフィルター（あいまい検索）
    const textFilters: Array<{ q: string; field: (r: ReservationListRow) => string }> = [
      { q: this.columnFilters.ownerName, field: (r) => r.owner_name },
      { q: this.columnFilters.ownerKana, field: (r) => r.owner_name_kana },
      { q: this.columnFilters.petName, field: (r) => r.animal_name },
      { q: this.columnFilters.petKana, field: (r) => r.animal_name_kana },
      { q: this.columnFilters.purpose, field: (r) => r.message },
    ];
    for (const tf of textFilters) {
      if (tf.q) {
        list = list.filter((r) => this.fuzzyIncludes(tf.field(r), tf.q));
      }
    }

    // 複数選択フィルター
    if (this.columnFilters.course.length > 0) {
      list = list.filter((r) => this.columnFilters.course.includes(r.course_name));
    }
    if (this.columnFilters.type.length > 0) {
      list = list.filter((r) => this.columnFilters.type.includes(r.animal_type));
    }

    // ソート：予約日を主キーとし、同一予約日内は予約時間を常に昇順、
    // 同一予約日・同一予約時間内はコース名を常に昇順で並べる。
    // 予約時間側のソート機能は撤去済み。降順時に list.reverse() を使うとサブキーも
    // 一緒に反転してしまうため、比較結果に dir を掛ける方式で明示的にソートする。
    // コース名の昇順は日本語の並び順を考慮して localeCompare("ja") を使用する。
    if (this.sortState.column === "date" && this.sortState.order) {
      const dir = this.sortState.order === "asc" ? 1 : -1;
      list.sort((a, b) => {
        if (a.date < b.date) return -1 * dir;
        if (a.date > b.date) return 1 * dir;
        if (a.time < b.time) return -1;
        if (a.time > b.time) return 1;
        return (a.course_name ?? "").localeCompare(b.course_name ?? "", "ja");
      });
    } else {
      // ソート未指定（解除状態）時の並び順。
      // 予約日・予約時間はサーバー側(datetime)で既に昇順のためその並びは維持し、
      // 同一予約日・同一予約時間が重なった場合のコース名昇順タイブレークのみ適用する。
      list.sort((a, b) => {
        if (a.date < b.date) return -1;
        if (a.date > b.date) return 1;
        if (a.time < b.time) return -1;
        if (a.time > b.time) return 1;
        return (a.course_name ?? "").localeCompare(b.course_name ?? "", "ja");
      });
    }

    return list;
  }

  // ページネーション：現在ページに対応する行のみ返す
  private get pagedRows(): ReservationListRow[] {
    const start = (this.currentPage - 1) * this.itemsPerPage;
    return this.displayRows.slice(start, start + this.itemsPerPage);
  }

  // 総ページ数（最低1ページ）
  private get totalPages(): number {
    return Math.max(1, Math.ceil(this.displayRows.length / this.itemsPerPage));
  }

  // ページ送りラベル（"1-10 of 16" 形式）
  private get pageRangeLabel(): string {
    const total = this.displayRows.length;
    if (total === 0) return "0 件";
    const start = (this.currentPage - 1) * this.itemsPerPage + 1;
    const end = Math.min(start + this.itemsPerPage - 1, total);
    return `${start}-${end} of ${total}`;
  }

  // 表示件数を変更したときの処理（1ページ目にリセット）
  private onItemsPerPageChange(value: number) {
    this.itemsPerPage = value;
    this.currentPage = 1;
  }

  // フィルター/ソートが何か適用されているか
  private get hasAnyFilter(): boolean {
    if (this.sortState.column) return true;
    if (this.columnFilters.course.length > 0) return true;
    if (this.columnFilters.type.length > 0) return true;
    return (
      !!this.columnFilters.ownerName ||
      !!this.columnFilters.ownerKana ||
      !!this.columnFilters.petName ||
      !!this.columnFilters.petKana ||
      !!this.columnFilters.purpose
    );
  }

  // あいまい検索用の正規化（全角半角・カナひらがな・大文字小文字を統一）
  private normalizeForSearch(str: string): string {
    if (str == null) return "";
    let s = String(str);
    s = s.replace(/[！-～]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0));
    s = s.replace(/　/g, " ");
    s = s.normalize("NFKC");
    s = s.replace(/[ぁ-ゖ]/g, (c) => String.fromCharCode(c.charCodeAt(0) + 0x60));
    s = s.toLowerCase();
    return s.trim();
  }

  private fuzzyIncludes(haystack: string, needle: string): boolean {
    const n = this.normalizeForSearch(needle);
    if (!n) return true;
    return this.normalizeForSearch(haystack).includes(n);
  }

  // ===== ソート =====
  // 予約時間側のソート機能は撤去済み。予約日のみソート可能。
  private onSortClick(column: "date") {
    if (this.sortState.column !== column) {
      this.sortState = { column, order: "asc" };
    } else if (this.sortState.order === "asc") {
      this.sortState = { column, order: "desc" };
    } else {
      this.sortState = { column: null, order: null };
    }
    this.currentPage = 1;
  }

  // ソートアイコンの矢印が「アクティブ（濃色）」かどうか
  private isSortActive(column: "date", order: "asc" | "desc"): boolean {
    return this.sortState.column === column && this.sortState.order === order;
  }

  // 期間指定カレンダー下部の「選択中」表示用フォーマット（YYYY/M/D、ゼロ埋めなし）
  // 抽出期間ラベル（formatDateSlash）と表示形式を揃える
  private formatSlash(value: string | Date): string {
    if (value instanceof Date) {
      return `${value.getFullYear()}/${value.getMonth() + 1}/${value.getDate()}`;
    }
    return this.formatDateSlash(value);
  }
  // Date → "YYYY-MM-DD"
  private toIsoDate(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }

  private toSchedule() {
    this.$router.push({ name: "Schedule" });
  }

  // 全条件クリア（ソートとカラムフィルターを一括解除、抽出期間は維持）
  // 実装時は API で最新情報を再取得する想定
  private async clearAllFilters() {
    this.columnFilters = {
      ownerName: "",
      ownerKana: "",
      petName: "",
      petKana: "",
      purpose: "",
      course: [],
      type: [],
    };
    this.sortState = { column: null, order: null };
    this.currentPage = 1;
    this.closeFilterPopover();
    await this.fetchReservations();
  }

  // ===== フィルターポップオーバー =====
  // テキスト系ポップオーバー表示時のラベル
  private get textPopoverLabel(): string {
    const labelMap: Record<string, string> = {
      ownerName: "名前で絞り込み",
      ownerKana: "名前(カナ)で絞り込み",
      petName: "どうぶつ名で絞り込み",
      petKana: "どうぶつ名(カナ)で絞り込み",
      purpose: "来院目的で絞り込み",
    };
    return labelMap[this.activePopoverKey ?? ""] ?? "";
  }

  private closeFilterPopover() {
    this.activePopoverKey = null;
  }

  private openFilterPopover(event: MouseEvent, key: string) {
    event.stopPropagation();
    // 同じものをクリックしたら閉じる
    if (this.activePopoverKey === key) {
      this.closeFilterPopover();
      return;
    }

    // テキスト系/チェックボックス系の仮入力値を初期化
    if (["ownerName", "ownerKana", "petName", "petKana", "purpose"].includes(key)) {
      this.popoverTextValue = (this.columnFilters as any)[key] as string;
    } else if (key === "course" || key === "type") {
      this.popoverCheckboxSelected = [...(this.columnFilters as any)[key]] as string[];
      this.popoverCheckboxOptions =
        key === "course"
          ? [...new Set(this.rows.map((r) => r.course_name).filter((v) => v))]
          : [...new Set(this.rows.map((r) => r.animal_type).filter((v) => v))];
    }

    // <th> の位置を基準にポップオーバーの座標を計算
    const th = (event.currentTarget as HTMLElement).closest("th") as HTMLElement;
    this.activePopoverKey = key;
    // 表示後にDOMが描画されてから位置調整するため nextTick
    this.$nextTick(() => this.positionPopover(th));

    // テキスト系なら入力欄にフォーカス
    if (["ownerName", "ownerKana", "petName", "petKana", "purpose"].includes(key)) {
      this.$nextTick(() => {
        const input = this.$refs.popoverInput as HTMLInputElement | undefined;
        input?.focus();
      });
    }
  }

  // <th> の真下を基本に画面内に収まるよう座標を計算
  private positionPopover(th: HTMLElement) {
    if (!th) return;
    const rect = th.getBoundingClientRect();
    // 実DOMから現在のポップオーバーサイズを取得
    const popEl = document.querySelector(".reservation-list-filter-popover") as HTMLElement | null;
    const popWidth = popEl?.offsetWidth ?? 240;
    const popHeight = popEl?.offsetHeight ?? 200;
    const margin = 8;
    const vw = document.documentElement.clientWidth;
    const vh = document.documentElement.clientHeight;

    let left = rect.left + 8;
    if (left + popWidth + margin > vw) {
      left = Math.max(margin, vw - popWidth - margin);
    }
    let top = rect.bottom + 4;
    if (top + popHeight + margin > vh) {
      const above = rect.top - popHeight - 4;
      top = above >= margin ? above : Math.max(margin, vh - popHeight - margin);
    }
    this.popoverStyle = { left: `${left}px`, top: `${top}px` };
  }

  // ポップオーバー内のテキスト入力欄のキー操作
  // IME変換中の Enter は無視する（変換確定の Enter で適用してしまわないように）
  private onPopoverInputKeydown(event: KeyboardEvent) {
    // IME変換中（compositionの最中）は無視
    if (event.isComposing || event.keyCode === 229) return;
    if (event.key === "Enter") {
      this.applyTextFilter(this.activePopoverKey, this.popoverTextValue);
    } else if (event.key === "Escape") {
      this.closeFilterPopover();
    }
  }

  // チェックボックスのトグル
  private togglePopoverCheckbox(value: string) {
    const idx = this.popoverCheckboxSelected.indexOf(value);
    if (idx >= 0) {
      this.popoverCheckboxSelected.splice(idx, 1);
    } else {
      this.popoverCheckboxSelected.push(value);
    }
  }

  private applyTextFilter(key: string | null, value: string) {
    if (!key) return;
    (this.columnFilters as any)[key] = value;
    this.currentPage = 1;
    this.closeFilterPopover();
  }
  private clearTextFilter(key: string | null) {
    if (!key) return;
    (this.columnFilters as any)[key] = "";
    this.popoverTextValue = "";
    this.currentPage = 1;
    this.closeFilterPopover();
  }
  private applyCheckboxFilter(key: string | null) {
    if (!key) return;
    (this.columnFilters as any)[key] = [...this.popoverCheckboxSelected];
    this.currentPage = 1;
    this.closeFilterPopover();
  }
  private clearCheckboxFilter(key: string | null) {
    if (!key) return;
    (this.columnFilters as any)[key] = [];
    this.popoverCheckboxSelected = [];
    this.currentPage = 1;
    this.closeFilterPopover();
  }

  // 外側クリック・スクロール・リサイズでポップオーバーを閉じる
  private onDocumentClick = (e: MouseEvent) => {
    // ポップオーバー要素やフィルターアイコン上のクリックは無視
    const target = e.target as HTMLElement | null;
    if (!target) return;
    if (target.closest(".reservation-list-filter-popover")) return;
    if (target.closest(".filter-icon")) return;
    this.closeFilterPopover();
  };
  private onWindowChange = () => this.closeFilterPopover();

  mounted() {
    document.addEventListener("click", this.onDocumentClick);
    window.addEventListener("scroll", this.onWindowChange, true);
    window.addEventListener("resize", this.onWindowChange);
  }

  beforeDestroy() {
    document.removeEventListener("click", this.onDocumentClick);
    window.removeEventListener("scroll", this.onWindowChange, true);
    window.removeEventListener("resize", this.onWindowChange);
    this.closeFilterPopover();
  }

  // ===== カレンダーモーダル =====
  private openCalendarModal() {
    // 適用中の範囲を Date 化してモーダル内の仮選択を初期化
    const startDate = this.appliedRange.start ? new Date(this.appliedRange.start) : null;
    const endDate = this.appliedRange.end ? new Date(this.appliedRange.end) : null;
    this.pendingRange = { start: startDate, end: endDate };
    this.showCalendarDialog = true;
  }
  // vc-date-picker (is-range) で1日目をクリックしたときの中間イベント。
  // 範囲確定前なので「指定範囲を抽出」ボタンを非活性にしておく。
  private onRangeDragging(range: { start: Date | null; end: Date | null } | null) {
    this.pendingRange = range ?? { start: null, end: null };
    this.isRangeDragging = true;
  }
  // vc-date-picker (is-range) で2日目をクリックして範囲が確定したときのイベント。
  private onRangeChange(range: { start: Date | null; end: Date | null } | null) {
    this.pendingRange = range ?? { start: null, end: null };
    this.isRangeDragging = false;
  }
  private cancelCalendar() {
    this.showCalendarDialog = false;
    this.isRangeDragging = false;
  }
  private async applyCalendarRange() {
    if (!this.pendingRange?.start || !this.pendingRange?.end) return;
    this.appliedRange = {
      start: this.toIsoDate(this.pendingRange.start),
      end: this.toIsoDate(this.pendingRange.end),
    };
    this.showCalendarDialog = false;
    await this.fetchReservations();
  }
  // ツールチップ用：tooltip-cell が省略表示（はみ出し）になっている場合のみ
  // 親 td に is-overflowing クラスを付与する
  private checkOverflow(event: MouseEvent) {
    const td = event.currentTarget as HTMLElement;
    const cell = td.querySelector(".tooltip-cell") as HTMLElement | null;
    if (!cell) return;
    if (cell.scrollWidth > cell.clientWidth) {
      td.classList.add("is-overflowing");
    } else {
      td.classList.remove("is-overflowing");
    }
  }

  // 「詳細」ボタン押下時。モーダルを開いて詳細APIを順に呼び出す
  private async openDetail(row: ReservationListRow) {
    this.detailRow = row;
    this.detailUserInfo = { email: "" };
    this.detailPetInfo = {
      animal_breed: "",
      gender: "",
      birthday: { year: "", month: "", day: "" },
    };
    this.showDetailDialog = true;
    await this.fetchDetail();
  }

  private closeDetailDialog() {
    this.showDetailDialog = false;
    this.detailRow = null;
  }

  // 詳細モーダル用に飼い主情報・どうぶつ情報を取得する
  // 必要なID（customer_id / member_id / patient_id / pet_id）はすべて
  // 一覧の行データ（getReservationsForList のレスポンス）から取得できる。
  private async fetchDetail() {
    if (!this.detailRow) return;
    try {
      this.detailLoading = true;
      // 飼い主情報
      if (this.detailRow.customer_id) {
        await this.fetchCustomerInfo(this.detailRow.customer_id);
      } else if (this.detailRow.member_id) {
        await this.fetchMemberInfo(this.detailRow.member_id);
      }
      // どうぶつ情報
      if (this.detailRow.patient_id) {
        await this.fetchPatientInfo(this.detailRow.patient_id);
      } else if (this.detailRow.pet_id && this.detailRow.member_id) {
        await this.fetchPetInfo(this.detailRow.member_id, this.detailRow.pet_id);
      }
    } catch (error) {
      console.error("[ReservationList] failed to fetch detail:", error);
    } finally {
      this.detailLoading = false;
    }
  }

  private async fetchCustomerInfo(customerId: string) {
    const res = await FacilityService.getCustomer(this.hospitalId, customerId);
    const customer = res?.data?.data?.[0];
    if (customer?.email) this.detailUserInfo.email = customer.email;
  }

  private async fetchMemberInfo(memberId: string) {
    const res = await MemberService.getMember(memberId);
    const member = res?.data?.data?.[0];
    if (member?.email) this.detailUserInfo.email = member.email;
  }

  private async fetchPatientInfo(patientId: string) {
    const res = await FacilityService.getPatient(this.hospitalId, patientId);
    const patient = res?.data?.data?.[0];
    if (!patient) return;
    this.detailPetInfo.animal_breed = patient.animal_breed ?? "";
    this.detailPetInfo.gender = patient.gender ?? "";
    this.detailPetInfo.birthday = this.parseBirthday(patient.birthday);
  }

  private async fetchPetInfo(memberId: string, petId: string) {
    const res = await MemberService.getPet(memberId, petId);
    const pet = res?.data?.data?.[0];
    if (!pet) return;
    this.detailPetInfo.animal_breed = pet.animal_breed ?? "";
    this.detailPetInfo.gender = pet.gender ?? "";
    this.detailPetInfo.birthday = this.parseBirthday(pet.birthday);
  }

  // "YYYYMMDD" を { year, month, day } に分解
  private parseBirthday(birthday: string | undefined): { year: string; month: string; day: string } {
    if (!birthday || birthday.length < 8) return { year: "", month: "", day: "" };
    return {
      year: birthday.substr(0, 4),
      month: birthday.substr(4, 2),
      day: birthday.substr(6, 2),
    };
  }

  // モーダル内の予約日時表示（"M/D (曜) H:mm 〜 H:mm"）
  // 一覧の行データ（date: "YYYY-MM-DD", time: "HH:mm", end_time: "HH:mm"）から組み立てる。
  // end_time が空文字（datetime_end が null 等で算出不可）の場合は開始時刻のみ表示。
  // スケジュール画面の予約情報モーダルと表示形式を揃えるため年は表示しない。
  private formatDetailDatetimeFromRow(row: ReservationListRow | null): string {
    if (!row || !row.date) return "";
    const weekdaysShort = ["日", "月", "火", "水", "木", "金", "土"];
    const d = new Date(`${row.date}T${row.time || "00:00"}:00`);
    if (isNaN(d.getTime())) return "";
    const m = d.getMonth() + 1;
    const day = d.getDate();
    const start = this.formatTimeForDisplay(row.time);
    const end = this.formatTimeForDisplay(row.end_time);
    const timeRange = end ? `${start} 〜 ${end}` : start;
    return `${m}/${day} (${weekdaysShort[d.getDay()]}) ${timeRange}`;
  }

  // モーダル内の生年月日表示
  private get detailBirthdayLabel(): string {
    const b = this.detailPetInfo.birthday;
    if (!b.year && !b.month && !b.day) return "";
    if (b.year === "0000" && b.month === "00" && b.day === "00") return "";
    const y = b.year && b.year !== "0000" ? `${b.year}年` : "";
    const m = b.month && b.month !== "00" ? `${b.month}月` : "";
    const d = b.day && b.day !== "00" ? `${b.day}日` : "";
    return `${y}${m}${d}`;
  }
  // 印刷ボタン押下時：0件なら専用アラート。いずれのブラウザも新規タブで生成済みPDFを開く。
  // さらに Chrome/Edge(デスクトップ)では、読み込み完了後にそのタブを印刷する。
  // blob URL は opener と同一オリジン扱いのため newWindow.print() はクロスオリジン
  // 制約を受けず、onload も発火する（Chromium 実機で確認済み）。
  private async openReservationPdf() {
    if (this.pdfDownloading) return;
    if (this.displayRows.length === 0) {
      this.showPrintEmpty = true;
      return;
    }
    // Chrome/Edge(デスクトップ)のみ印刷ダイアログを自動表示する。判定は
    // 非同期処理(await)の前に行う（後述のポップアップブロック対策と同じ理由）。
    const autoPrint = this.isChromiumDesktop();
    // ユーザー操作直後に同期で空タブを開く。await の後に window.open すると
    // ユーザー起動とみなされずポップアップブロックされるため、ここで開いておく。
    const newWindow = window.open("", "_blank");
    if (!newWindow) {
      this.showPrintPopupBlocked = true;
      return;
    }
    this.pdfDownloading = true;
    try {
      const rows = this.displayRows.map((row) => ({
        reservation_date: this.formatDateJa(row.date),
        course_name: row.course_name,
        reservation_time: this.formatTimeRange(row.time, row.end_time),
        owner_name: row.owner_name,
        owner_name_kana: row.owner_name_kana,
        animal_name: row.animal_name,
        animal_name_kana: row.animal_name_kana,
        animal_type: row.animal_type,
        visit_purpose: row.message,
      }));
      const payload = {
        hospital_name: FacilityModule.hospitalName,
        period_from: this.appliedRange.start,
        period_to: this.appliedRange.end,
        rows,
      };
      const res = await FacilityService.getReservationListPdf(this.hospitalId, payload);
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      if (autoPrint) {
        // Chrome/Edge: 生成済みPDFを新規タブに表示し、描画準備が整い次第そのタブを印刷する。
        // 固定タイムアウトだと「速い端末では無駄に待ち／遅い端末では早すぎて空白印刷」
        // になりやすいため、readyState と PDF embed の出現をポーリングして
        // 準備完了を検知した瞬間に print する（体感速度優先）。検知できない場合に
        // 備え、上限を超えたら未検知でも強制的に print する（printed ガードで二重防止）。
        const MIN_WAIT_MS = 300; // 描画開始前の早すぎる print を避ける最小待機
        const POLL_INTERVAL_MS = 100; // 準備完了チェックの間隔
        const MAX_WAIT_MS = 2000; // これを超えたら未検知でも print する安全装置
        let printed = false;
        const doPrint = () => {
          if (printed) return;
          printed = true;
          try {
            newWindow.focus();
            newWindow.print();
          } catch {
            /* PDFビューアの状態によっては失敗し得る。その場合はタブ表示のみ。 */
          }
        };
        // newWindow.document を読めるのは blob URL が opener と同一オリジン扱いのため。
        // 例外時（描画途中でアクセス不可等）は「未準備」とみなしポーリングを継続する。
        const isReady = (): boolean => {
          try {
            const doc = newWindow.document;
            return doc.readyState === "complete" || !!doc.querySelector("embed");
          } catch {
            return false;
          }
        };
        const startedAt = Date.now();
        const poll = () => {
          if (printed) return;
          if (Date.now() - startedAt >= MAX_WAIT_MS) {
            doPrint(); // 安全装置: 上限超過で未検知でも強制実行
            return;
          }
          if (isReady()) {
            doPrint(); // 準備完了を検知 → 即 print
            return;
          }
          window.setTimeout(poll, POLL_INTERVAL_MS);
        };
        // 逆タブナビング対策: 開いたタブから元の管理画面タブを操作させない。
        // opener を null にしても、同一オリジン(blob URL)の親→子 document 参照や
        // newWindow.print() は妨げられないため、下記のポーリング/印刷には影響しない。
        newWindow.opener = null;
        newWindow.location.href = url;
        window.setTimeout(poll, MIN_WAIT_MS); // 最低 MIN_WAIT_MS 待ってから検知開始
      } else {
        // Firefox/Safari 等: 新規タブでPDFを開くだけ（自動印刷しない）
        // 逆タブナビング対策: 開いたタブから元の管理画面タブを操作させない
        newWindow.opener = null;
        newWindow.location.href = url;
      }
      // タブが読み込む前に blob URL を破棄しないよう、解放を十分に遅延させる
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (error) {
      console.error("[ReservationList] failed to open reservation list pdf:", error);
      newWindow.close();
      this.showPrintError = true;
    } finally {
      this.pdfDownloading = false;
    }
  }

  // Chrome/Edge(デスクトップの Chromium 系)かどうかを判定する。
  // Firefox/Safari は userAgentData 非対応、iOS は中身が WebKit(=Safari 相当)の
  // ため、いずれも印刷ダイアログ自動表示の対象外(false)として扱う。
  private isChromiumDesktop(): boolean {
    const uaData = (navigator as any).userAgentData;
    if (uaData && Array.isArray(uaData.brands)) {
      if (uaData.mobile) return false;
      // "Chromium" は Brave 等 Chromium 系全般の brand に含まれるため対象にしない。
      // Google Chrome / Microsoft Edge のみを自動印刷の対象とする。
      return uaData.brands.some((b: { brand: string }) => /Google Chrome|Microsoft Edge/.test(b.brand));
    }
    // フォールバック(userAgentData 非対応 = 実質 Firefox/Safari)
    const ua = navigator.userAgent;
    const isIOS = /iPhone|iPad|iPod/.test(ua);
    return !isIOS && /\b(?:Chrome|Edg)\//.test(ua) && !/Firefox|OPR/.test(ua);
  }

  // CSVエクスポート（フィルター・ソート適用後の displayRows 全件を出力）
  // スケジュール画面の getReservationCsvData と同じ方式（Papa.unparse → SJIS変換 → Blobダウンロード）
  // 違い: ファイル名は「予約一覧.csv」固定、出力項目は一覧画面の9列（詳細列を除く）
  private exportCsv() {
    if (this.csvDownloading) return;
    this.csvDownloading = true;
    try {
      const jsonDataList = this.displayRows.map((row) => ({
        予約日: this.formatDateJa(row.date),
        コース名: row.course_name,
        開始時間: this.formatTimeForDisplay(row.time),
        終了時間: this.formatTimeForDisplay(row.end_time),
        名前: row.owner_name,
        "名前(カナ)": row.owner_name_kana,
        どうぶつ名: row.animal_name,
        "どうぶつ名(カナ)": row.animal_name_kana,
        種別: row.animal_type,
        来院目的: row.message,
      }));
      const config = {
        delimiter: ",",
        header: true,
        newline: "\r\n",
      };
      const delimiterString = Papa.unparse(jsonDataList, config);
      const strArray = Encoding.stringToCode(delimiterString);
      const convertedArray = Encoding.convert(strArray, "SJIS", "UNICODE");
      const UintArray = new Uint8Array(convertedArray);
      const blob = new Blob([UintArray], { type: "text/csv" });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = "予約一覧.csv";
      link.click();
      window.URL.revokeObjectURL(link.href);
    } finally {
      this.csvDownloading = false;
    }
  }
}
